"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn(
      "hp-fixed hp-inset-0 hp-z-50 hp-bg-black/80 !hp-block",
      className,
    )}
    {...props}
  >
    {" 123 "}
  </DrawerPrimitive.Overlay>
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ faqProps, className, children, onClose, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        "hp-fixed hp-inset-x-0 hp-bottom-0 hp-mt-24 hp-flex hp-h-auto hp-flex-col hp-rounded-t-[10px] hp-border hp-bg-background",
        className,
      )}
      style={{ zIndex: 99999 }}
      {...props}
    >
      <div className="hp-mx-auto hp-mt-4 hp-h-2 hp-rounded-full hp-bg-muted" />
      {children}
      <DrawerPrimitive.Close
        className={cn(
          "hp-absolute hp-p-2 hp-right-4 hp-top-4 hp-rounded-full hp-opacity-70 hp-ring-offset-background hp-transition-opacity hover:hp-opacity-100 focus:hp-outline-none focus:hp-ring-2 focus:hp-ring-ring focus:hp-ring-offset-2 disabled:hp-pointer-events-none data-[state=open]:hp-bg-accent data-[state=open]:hp-text-muted-foreground",
          faqProps,
        )}
        onClick={() => onClose?.()}
      >
        <X className="hp-h-6 hp-w-6" />
        <span className="hp-sr-only">Close</span>
      </DrawerPrimitive.Close>
    </DrawerPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "hp-grid hp-gap-1.5 hp-p-4 hp-text-center sm:hp-text-left",
      className,
    )}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("hp-mt-auto hp-flex hp-flex-col hp-gap-2 hp-p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "hp-text-lg hp-font-semibold hp-leading-none hp-tracking-tight",
      className,
    )}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("hp-text-sm hp-text-muted-foreground", className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
